/**
 * Bulk status changes on a Survey Project's course sections — reopening closed surveys, and
 * taking published ones live.
 *
 * Both are one request. The selection the admin built in the bulk-select modal is sent as the
 * filter set that resolves it, and the backend does the work in a Celery task, reporting every
 * section's outcome in the task result. This module is the seam between the modal and those two
 * endpoints: what a submit needs, and what the menu items are gated on.
 *
 * It used to be the transport itself — a fan-out over the per-section endpoints, four requests at
 * a time, with client-side Select All resolution, a progress counter and a local task lock. All of
 * that is gone: `POST /slots/{slotId}/sections/bulk_reopen` and `.../bulk_go_live` (Backend #2130)
 * resolve the selection server-side, run each section in isolation, and persist per-section
 * outcomes to the task result. Three things the fan-out could not do and no longer have to be
 * worked around here:
 *
 *   - **Select All needs no ids.** It is `filters` plus `section_id not_in [exclusions]`, resolved
 *     once against the same query the selection list used. No paging, no page cap, no dedup around
 *     an unstable sort.
 *   - **Per-project work happens once.** The division report index, the export-snapshot purge and
 *     the auto-close reschedule are per Survey Project, not per section.
 *   - **Outcomes are structured.** `reason` is a fixed vocabulary, so a section that went live but
 *     did not finish provisioning is reported as such instead of being guessed at from the shape
 *     of an error message.
 *
 * Spec: docs/specs/active/bulk-reopen-closed-section-surveys/spec.md
 */

import {
  bulkGoLiveSlotSections,
  bulkReopenSlotSections,
  type BulkReopenTimestamps,
  type BulkSectionStatusSelection,
  type ReopenTargetStatus
} from '../../../APIManager/BulkAddCourseSectionManger'

export type { BulkReopenTimestamps, BulkSectionStatusSelection, ReopenTargetStatus }

/**
 * Above this many sections the confirm step warns before proceeding. Not a ceiling: acting on a
 * whole term is the case these features exist for. It exists because the writes are irreversible
 * and an accidental Select All should get a second look.
 */
export const BULK_ACTION_WARN_THRESHOLD = 100

export const isBulkReopenEnabled = (): boolean => true
export const isBulkGoLiveEnabled = (): boolean => true

/**
 * The publish branch of Bulk Reopen — reopen into Upcoming, opening on a chosen start date.
 *
 * **On.** It was off while the endpoint had no `target_status` / `start_timestamp`: `msgspec.convert`
 * does not forbid unknown fields, so a scheduled batch would have reopened every section Live
 * immediately while the modal said next month — the one outcome nothing can undo.
 *
 * That gate is closed. Backend #2151 merged and is an ancestor of `test` and `prod`; both deployed
 * successfully on 2026-08-27 (`a54465fd`, `5eec1a90`). `target_status` is validated explicitly
 * there, so an unrecognised value is a `400` rather than a silent live reopen.
 *
 * One boolean rather than per-environment config because the frontend has no environment-aware flag
 * mechanism — this turns the branch on for dev, test and prod together, which is what the contract
 * being live everywhere allows.
 */
export const isBulkReopenScheduleEnabled = (): boolean => true

/** What a `202` from either endpoint carries: the task to follow, and what to call it. */
export interface BulkSectionStatusTask {
  taskId: string
  message?: string
}

const readTaskEnvelope = (data: any): BulkSectionStatusTask | null => {
  const taskId = data?.taskId
  if (typeof taskId !== 'string' || taskId.trim() === '') return null
  return { taskId, message: typeof data?.message === 'string' ? data.message : undefined }
}

/**
 * Starts a bulk reopen and returns the task to follow, or `null` when the response carried no task
 * id — the caller reports that rather than leaving the admin watching nothing.
 */
export const startBulkReopen = async (
  slotId: string,
  selection: BulkSectionStatusSelection,
  dates: BulkReopenTimestamps,
  targetStatus: ReopenTargetStatus = 'live'
): Promise<BulkSectionStatusTask | null> => {
  const response = await bulkReopenSlotSections(slotId, selection, dates, targetStatus)
  return readTaskEnvelope(response?.data)
}

/** Starts a bulk go live. No dates: the sections already carry their own. */
export const startBulkGoLive = async (
  slotId: string,
  selection: BulkSectionStatusSelection
): Promise<BulkSectionStatusTask | null> => {
  const response = await bulkGoLiveSlotSections(slotId, selection)
  return readTaskEnvelope(response?.data)
}
